/**
 * Element hooks for accessing Zustand document store state.
 *
 * PERFORMANCE PATTERNS:
 *
 * 1. Primitive hooks (no useShallow needed):
 *    - useElementCount(), useHasElements(), useElementDesignatorsCount()
 *    - Return primitives (number, boolean) - no shallow comparison overhead
 *    - Prefer these when you only need counts or existence checks
 *
 * 2. UIDs pattern (recommended for lists):
 *    - useElementUids(), useNonBranchPointElementUids()
 *    - Return string[] of UIDs, then use useElementByUid() for each item
 *    - Each individual hook is O(1) comparison vs O(n) shallow compare
 *
 * 3. Object/array hooks (use with caution):
 *    - useAllElements(), useNonBranchPointElements()
 *    - Require useShallow for O(n) shallow comparison on every store update
 *    - Can cause performance issues with large collections (1000+ items)
 *
 * When to use each pattern:
 * - Need count/existence? → Use primitive hooks (useElementCount, useHasElements)
 * - Need to iterate? → Use UIDs pattern (useElementUids + useElementByUid)
 * - Need full map? → Use object hooks with caution (useAllElements)
 */
import type {
    IElementData,
    IElementsMap,
    IPartVersionData,
    IPropertiesMap,
    IPropertyData,
    PartSourcingProperties,
} from "@buildwithflux/models";
import {useShallow} from "zustand/react/shallow";

import {useFluxServices} from "../../../../injection/hooks";
import {select} from "../selectors";

/**
 * Hook to get all elements
 * Uses shallow comparison to prevent unnecessary re-renders
 */
export const useAllElements = (): IElementsMap | undefined => {
    return useFluxServices().useDocumentStore(useShallow(select.elements.all));
};

/**
 * Hook to get an element by UID
 * @param uid Element UID to retrieve
 */
export const useElementByUid = (uid: string): IElementsMap[string] | undefined => {
    return useFluxServices().useDocumentStore(select.elements.byUid(uid));
};

/**
 * Hook to get multiple elements by UIDs
 * @param uids Element UIDs to retrieve
 */
export const useElementsByUids = (uids: string[]): Array<IElementsMap[string]> => {
    return useFluxServices().useDocumentStore(useShallow(select.elements.byUids(uids)));
};

/**
 * Hook to get all element UIDs
 */
export const useElementUids = (): string[] => {
    return useFluxServices().useDocumentStore(useShallow(select.elements.uids));
};

/**
 * Hook to get element count
 */
export const useElementCount = (): number => {
    return useFluxServices().useDocumentStore(select.elements.count);
};

/**
 * Hook to check if document has any elements
 */
export const useHasElements = (): boolean => {
    return useFluxServices().useDocumentStore(select.elements.hasElements);
};

/**
 * Hook to get element designators (labels) from the Zustand store.
 *
 * MEMOIZATION: This selector requires `useShallow` because:
 * - Returns an array of strings that is recomputed on each call
 * - Without shallow comparison, a new array reference would cause unnecessary re-renders
 * - The shallow comparison ensures re-renders only occur when array contents actually change
 *
 * PERFORMANCE NOTE: If you only need to check if designators exist or get the count,
 * prefer `useElementDesignatorsCount()` which returns a primitive and avoids the
 * O(n) shallow comparison overhead.
 *
 * @returns Array of element designator labels
 */
export const useElementDesignators = (): string[] => {
    return useFluxServices().useDocumentStore(useShallow(select.elements.designators));
};

/**
 * Hook to get the count of element designators (labels) from the Zustand store.
 *
 * PERFORMANCE: This hook returns a primitive (number) instead of an array,
 * avoiding the O(n) shallow comparison overhead of `useShallow`. Use this
 * when you only need to check if elements exist or get the count.
 *
 * @returns The number of element designators
 */
export const useElementDesignatorsCount = (): number => {
    return useFluxServices().useDocumentStore(select.elements.designatorsCount);
};

/**
 * Hook to get non-branch point elements from the Zustand store.
 * Filters out branch point elements which are internal wiring nodes.
 *
 * MEMOIZATION: This selector requires `useShallow` because:
 * - Returns an array of complex element objects that is recomputed on each call
 * - Each element object contains nested properties (part_version_data_cache, properties, etc.)
 * - Without shallow comparison, a new array reference would cause unnecessary re-renders
 * - The shallow comparison ensures re-renders only occur when array contents actually change
 *
 * @returns Array of non-branch point element data objects
 */
export const useNonBranchPointElements = (): Array<IElementsMap[string]> => {
    return useFluxServices().useDocumentStore(useShallow(select.elements.nonBranchPointElements));
};

/**
 * Hook to get non-branch point element UIDs - memoized
 */
export const useNonBranchPointElementUids = (): string[] => {
    return useFluxServices().useDocumentStore(useShallow(select.elements.nonBranchPointElementUids));
};

/**
 * Hook to get the array of part UIDs used in the current document.
 * Extracts part UIDs from all elements that have a part_version.
 * Delegates to the memoized partUidsInDocument selector.
 * @returns An array of unique part UIDs used in the document
 */
export const usePartUidsInDocument = (): string[] => {
    return useFluxServices().useDocumentStore(useShallow(select.elements.partUidsInDocument));
};

/**
 * @deprecated Use computeFilteredPartVersions from selectors/elements/derived instead
 */
export {computeFilteredPartVersions} from "../selectors/elements/derived";

/**
 * Hook to get part version data for parts that need updates.
 * Extracts unique part versions from document elements and optionally filters by part UIDs.
 * This replaces the Redux selector `useSelectPartDataForUpdate`.
 *
 * @param partUidsForUpdate Array of part UIDs to filter by (only used when selectAll is false)
 * @param selectAll If true, returns all unique part versions; if false, filters by partUidsForUpdate
 * @returns Array of part version data for parts that need updates
 */
export const usePartDataForUpdate = (partUidsForUpdate: string[], selectAll: boolean): IPartVersionData[] => {
    return useFluxServices().useDocumentStore(
        useShallow(select.elements.partDataForUpdate(partUidsForUpdate, selectAll)),
    );
};

/**
 * Hook to check if an element (or any of its parent modules) has "Exclude from BOM" = true.
 * This walks up the nested element hierarchy checking each level.
 * @param elementUid The element UID to check (can be a nested UID like "baseUid__nestedUid")
 * @returns True if any element in the hierarchy has "Exclude from BOM" = true
 */
export const useIsExcludedFromBomWithModules = (elementUid: string): boolean => {
    return useFluxServices().useDocumentStore(select.elements.isExcludedFromBomWithModules(elementUid));
};

/**
 * Hook to get part sourcing properties for an element from the Zustand store.
 * Returns manufacturer name, manufacturer part number, part name, and part UID.
 * @param elementUid The element UID to get sourcing properties for
 * @returns PartSourcingProperties or null if element not found
 */
export const useElementPartSourcingPropertiesFromStore = (elementUid: string): PartSourcingProperties | null => {
    return useFluxServices().useDocumentStore(useShallow(select.elements.partSourcingProperties(elementUid)));
};

/**
 * Hook to get an element's label from the Zustand store
 * @param elementUid The element UID to get the label for
 * @returns The element's label or undefined if not found
 */
export const useElementLabel = (elementUid: string): string | undefined => {
    return useFluxServices().useDocumentStore(select.elements.labelByUid(elementUid));
};

/**
 * Hook to get an element's lock state from the Zustand store
 * @param elementUid The element UID to check
 * @returns The element's locked state or undefined if not found
 */
export const useElementLockState = (elementUid: string): boolean | undefined => {
    return useFluxServices().useDocumentStore(select.elements.lockedByUid(elementUid));
};

/**
 * Hook to get an element's metamodule data from the Zustand store.
 * Delegates to the elements.elementMetaModule selector.
 * Uses shallow comparison to avoid unnecessary re-renders when the metamodule object reference changes.
 * @param elementUid The element UID to get metamodule data for
 * @returns The element's metamodule data or null if not found
 */
export const useElementMetaModule = (elementUid: string): IElementData["metamodule"] | null => {
    return useFluxServices().useDocumentStore(useShallow(select.elements.elementMetaModule(elementUid)));
};

/**
 * Hook to get an element's properties from the Zustand store
 * Uses shallow comparison to avoid unnecessary re-renders
 * @param elementUid The element UID to get properties for
 * @returns The element's properties map or undefined if not found
 */
export const useElementProperties = (elementUid: string): IPropertiesMap | undefined => {
    return useFluxServices().useDocumentStore(useShallow(select.elements.propertiesByUid(elementUid)));
};

/**
 * Hook to get an element's part version data cache from the Zustand store.
 *
 * MEMOIZATION: This selector requires special memoization handling because:
 * - The selector returns a complex object (IPartVersionData) with nested properties
 * - Properties like `extracted_element_cache`, `terminals`, and `properties` are deeply nested objects
 * - Helper functions like `getDefaultPartProperty` may return new objects even within the same parent object
 * - Without `useShallow`, this would cause unnecessary re-renders on every store update
 * - The shallow comparison ensures re-renders only occur when the top-level reference changes
 *
 * @param elementUid The element UID to get part version data cache for
 * @returns The element's part version data cache or undefined if not found
 */
export const useElementPartVersionDataCache = (elementUid: string): IPartVersionData | undefined => {
    return useFluxServices().useDocumentStore(useShallow(select.elements.partVersionDataCacheByUid(elementUid)));
};

/**
 * Hook to get an element's part UID from the Zustand store
 * @param elementUid The element UID to get the part UID for
 * @returns The element's part UID or undefined if not found
 */
export const useElementPartUid = (elementUid: string): string | undefined => {
    return useFluxServices().useDocumentStore(select.elements.partUidByUid(elementUid));
};

/**
 * Hook to get an element's diagram position from the Zustand store
 * Uses shallow comparison to avoid unnecessary re-renders
 * @param elementUid The element UID to get the diagram position for
 * @returns The element's diagram position or undefined if not found
 */
export const useElementDiagramPosition = (elementUid: string) => {
    return useFluxServices().useDocumentStore(useShallow(select.elements.diagramPositionByUid(elementUid)));
};

/**
 * Hook to get an element's symbol placements from the Zustand store
 * Uses shallow comparison to avoid unnecessary re-renders
 * @param elementUid The element UID to get symbol placements for
 * @returns The element's symbol placements or undefined if not found
 */
export const useElementSymbolPlacements = (elementUid: string): IElementData["symbol_placements"] => {
    return useFluxServices().useDocumentStore(useShallow(select.elements.symbolPlacementsByUid(elementUid)));
};

/**
 * Hook to get an element's part version data cache attribute from the Zustand store
 * This is a generic hook that can retrieve any attribute from the part_version_data_cache
 * Delegates to the elements.elementPartVersionDataCacheAttribute selector.
 * NOTE: Uses useShallow for proper memoization since attributes can be objects/arrays (e.g., controls, properties)
 * @param elementUid The element UID
 * @param attributeName The attribute name to retrieve from part_version_data_cache
 * @returns The attribute value or undefined
 */
export const useElementPartVersionDataCacheAttribute = <T>(
    elementUid: string,
    attributeName: keyof IPartVersionData,
): T | undefined => {
    return useFluxServices().useDocumentStore(
        useShallow(select.elements.elementPartVersionDataCacheAttribute<T>(elementUid, attributeName)),
    );
};

/**
 * Hook to get the element visibility toggle map from the Zustand store.
 * This determines which properties and metrics should be visible for an element.
 * Uses shallow comparison to avoid unnecessary re-renders
 * @param elementUid The element UID
 * @param currentUserUid The current user's UID (optional)
 * @returns A map of property/metric UIDs to their visibility state
 */
export const useElementVisibilityToggleMap = (
    elementUid: string,
    currentUserUid: string | undefined,
): {[key: string]: boolean} => {
    return useFluxServices().useDocumentStore(
        useShallow(select.elements.visibilityToggleMap(elementUid, currentUserUid)),
    );
};

/**
 * Hook to get the visible properties for an element from the Zustand store.
 * Returns properties that are marked as visible in the visibility toggle map, sorted by order.
 * Uses shallow comparison to avoid unnecessary re-renders
 * @param elementUid The element UID
 * @param currentUserUid The current user's UID (optional)
 * @returns Array of visible properties sorted by order
 */
export const useElementVisibleProperties = (
    elementUid: string,
    currentUserUid: string | undefined,
): IPropertyData[] => {
    return useFluxServices().useDocumentStore(
        useShallow(select.elements.visibleProperties(elementUid, currentUserUid)),
    );
};

/**
 * @deprecated Use computeNumVisibleProperties from selectors/elements/derived instead
 */
export {computeNumVisibleProperties} from "../selectors/elements/derived";

/**
 * Hook to get the count of visible properties for an element from the Zustand store.
 * @param elementUid The element UID
 * @param currentUserUid The current user's UID (optional)
 * @returns The number of visible properties
 */
export const useElementNumVisibleProperties = (elementUid: string, currentUserUid: string | undefined): number => {
    return useFluxServices().useDocumentStore(select.elements.numVisibleProperties(elementUid, currentUserUid));
};

/**
 * Hook to get an array of all unique values for a property across all elements.
 * This is used for dynamic autocomplete suggestions.
 * Uses shallow comparison to avoid unnecessary re-renders
 * @param propertyName The property name to search for
 * @param shouldSkip If true, returns an empty array (for performance optimization)
 * @returns Array of unique property values
 */
export const useElementPropertyValues = (propertyName: string, shouldSkip: boolean): string[] => {
    return useFluxServices().useDocumentStore(
        useShallow((state) => {
            if (shouldSkip) {
                return [];
            }
            return select.elements.propertyValues(propertyName)(state);
        }),
    );
};

/**
 * Hook to get a specific attribute from an element
 * This is a generic hook that can retrieve any attribute from an element
 * Uses shallow comparison to avoid unnecessary re-renders for object/array attributes
 * @param attributeName The attribute name to retrieve from the element
 * @param elementUid The element UID (optional)
 * @param fallbackValue Optional fallback value if attribute is not found
 * @returns The attribute value or undefined
 */
export const useElementAttribute = <T>(
    attributeName: keyof IElementData,
    elementUid?: string,
    fallbackValue?: T,
): T | undefined => {
    return useFluxServices().useDocumentStore(
        useShallow(select.elements.elementAttribute<T>(attributeName, elementUid, fallbackValue)),
    );
};

/**
 * Hook to get an element's 3D model asset storage name from the Zustand store
 * @param elementUid - The element UID
 * @returns The 3D model asset storage name or undefined
 */
export const useElement3DModelAssetStorageName = (elementUid: string): string | undefined => {
    return useFluxServices().useDocumentStore(select.elements.element3DModelAssetStorageName(elementUid));
};

/**
 * Hook to get an element's footprint asset storage name from the Zustand store
 * @param elementUid - The element UID
 * @returns The footprint asset storage name or undefined
 */
export const useElementFootprintAssetStorageName = (elementUid: string): string | undefined => {
    return useFluxServices().useDocumentStore(select.elements.elementFootprintAssetStorageName(elementUid));
};

/**
 * @deprecated Use computeElectricalDocumentSubset from selectors/document/derived instead
 */
export {computeElectricalDocumentSubset} from "../selectors/document/derived";

/**
 * @deprecated Use computeElementPropertyEvaluatedValue from selectors/elements/derived instead
 */
export {computeElementPropertyEvaluatedValue} from "../selectors/elements/derived";

/**
 * Hook to get the evaluated value of an element's property.
 * This evaluates expressions in property values using the element's context.
 * Uses memoization to prevent expensive expression evaluation on every store update.
 * @param elementUid The element UID
 * @param propertyKey The property key (name or uid) to evaluate
 * @returns The evaluated property value as a string, or undefined if not evaluable
 */
export const useElementPropertyEvaluatedValue = (elementUid: string, propertyKey: string): string | undefined => {
    return useFluxServices().useDocumentStore((state) =>
        select.elements.selectElementPropertyEvaluatedValue(state, elementUid, propertyKey),
    );
};

/**
 * Hook to get the symbol designator suffixes from the Zustand store.
 * Extracts unique symbol names from terminal elements.
 * Uses shallow comparison to avoid unnecessary re-renders
 */
export const useSymbolDesignatorSuffixes = (): string[] => {
    return useFluxServices().useDocumentStore(useShallow(select.elements.symbolDesignatorSuffixes));
};

/**
 * Hook to get the merged visibility toggle map for selected elements.
 * For single element selection, returns the element's visibility map.
 * For multi-selection, merges visibility maps across all selected elements,
 * marking properties with different visibilities as "mixed" (value 2).
 * Uses shallow comparison to avoid unnecessary re-renders
 * @param selectedObjectUids Array of selected object UIDs
 * @param selectedElementsCount Number of selected elements
 * @param parentUid The parent element UID (used for single selection)
 * @param currentUserUid The current user's UID (optional)
 * @returns A map of property names to their visibility state (boolean or 2 for mixed)
 */
export const useMergedVisibilityToggleMap = (
    selectedObjectUids: string[],
    selectedElementsCount: number,
    parentUid: string,
    currentUserUid: string | undefined,
): {[key: string]: boolean | number} => {
    return useFluxServices().useDocumentStore(
        useShallow(
            select.elements.mergedVisibilityToggleMap(
                selectedObjectUids,
                selectedElementsCount,
                parentUid,
                currentUserUid,
            ),
        ),
    );
};
